import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, MapPin } from "lucide-react";
import { PROPERTY, IMAGES, RIBBON, CTA_LABEL } from "../../data";

export const Hero = ({ onRequestPackage }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.42, 0.7]);

  return (
    <section id="overview" ref={ref} className="relative min-h-[94vh] flex items-center overflow-hidden">
      <motion.div className="absolute inset-0 z-0" style={{ y }}>
        <img
          src={IMAGES.hero}
          alt="Aerial view of a wooded Virginia waterfront homesite in morning light"
          className="w-full h-[120%] object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <motion.div className="absolute inset-0 z-10 bg-navy-dark" style={{ opacity: overlayOpacity }} />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-navy-dark/80 via-transparent to-navy-dark/30" />

      <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-10 w-full pt-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-2 text-white/85 mb-6">
            <MapPin size={16} className="text-water" />
            <span className="label-caps text-xs">0.91 Acre Homesite · {PROPERTY.county}</span>
          </div>

          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-white leading-[1.04] tracking-tight">
            {PROPERTY.name}
            <span className="block font-normal">Lot 17-L</span>
          </h1>

          <div data-testid="hero-price-badge" className="mt-6 inline-flex flex-wrap items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-3">
            <span className="label-caps text-[10px] text-white/70">Current Asking Price</span>
            <span className="font-serif text-2xl md:text-3xl text-white leading-none">{PROPERTY.price}</span>
            <span
              data-testid="hero-price-reduced-badge"
              className="label-caps text-[9px] tracking-[0.18em] text-white bg-water/80 rounded-full px-2.5 py-1 whitespace-nowrap"
            >
              Price Reduced · October 2026
            </span>
          </div>
          <div
            data-testid="hero-previous-price"
            className="mt-2 text-sm text-white/60 font-light"
          >
            Previously offered at <span className="line-through">{PROPERTY.previousPrice}</span>
          </div>

          <p className="mt-5 text-lg md:text-xl text-white/85 font-light max-w-2xl">
            0.91-acre homesite in Bay Quarter Shores now offered at {PROPERTY.price} with comprehensive
            seller due diligence.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-request-package-btn"
              onClick={onRequestPackage}
              className="bg-water text-white rounded-full px-8 py-3.5 font-medium hover:bg-water-soft transition-colors shadow-lg focus-visible:ring-2 focus-visible:ring-water focus-visible:outline-none"
            >
              {CTA_LABEL}
            </button>
            <button
              data-testid="hero-explore-btn"
              onClick={() => document.getElementById("property")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-white/10 backdrop-blur-md border border-white/30 text-white rounded-full px-8 py-3.5 font-medium hover:bg-white/20 transition-colors focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"
            >
              Explore Property
            </button>
          </div>

          <p data-testid="hero-supporting-text" className="mt-6 text-sm text-white/70 font-light">
            Private Sale (FSBO) &bull; Buyer agents welcome &bull; Closing handled through a licensed Virginia title company
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 text-white/60"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.div>
    </section>
  );
};

export const InfoRibbon = () => (
  <div className="relative z-30 -mt-16 md:-mt-20">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="backdrop-blur-xl bg-white/90 border border-warm-border rounded-3xl shadow-[0_20px_60px_-24px_rgba(11,37,69,0.25)] p-6 md:p-8"
        data-testid="info-ribbon"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-6 gap-x-4 divide-warm-border">
          {RIBBON.map((r) => (
            <div key={r.label} className="text-center lg:border-r lg:last:border-r-0 border-warm-border/60 px-2">
              <div className="label-caps text-[10px] text-water">{r.label}</div>
              <div className="mt-1.5 text-navy font-semibold leading-snug text-sm md:text-base">{r.value}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </div>
);
